import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Overlay } from './Overlay';
import type { OverlayProps } from './Overlay';
interface StripeOverlayContextProps {
  setCustomText: (customText?: OverlayProps['customText']) => void;
  setShow: (show: boolean, options?: {
    variant?: StripeOverlayVariant;
    customText?: OverlayProps['customText'];
  }) => void;
  setVariant: (variant: StripeOverlayVariant) => void;
  show: boolean;
  variant: StripeOverlayVariant;
}
export type StripeOverlayVariant = 'initial' | 'pending' | 'error';
export const StripeOverlayContext = createContext<StripeOverlayContextProps>({
  show: false,
  setShow: () => {},
  variant: 'initial',
  setVariant: () => {},
  setCustomText: () => {}
});
export const StripeOverlayProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState<StripeOverlayVariant>('initial');
  const [customText, setCustomText] = useState<OverlayProps['customText']>();
  const setShowWithVariant = useCallback((showVariant: boolean, options: {
    variant?: StripeOverlayVariant;
    customText?: OverlayProps['customText'];
  } = {}) => {
    setShow(showVariant);
    const {
      variant: variantOption,
      customText: customTextOption
    } = options;
    if (variantOption) {
      setVariant(variantOption);
    }
    if (customTextOption) {
      setCustomText(customTextOption);
    }
  }, []);
  const value = useMemo(() => ({
    show,
    setShow: setShowWithVariant,
    variant,
    setVariant,
    setCustomText
  }), [show, setShowWithVariant, variant]);
  return <StripeOverlayContext.Provider value={value} data-sentry-element="StripeOverlayContext.Provider" data-sentry-component="StripeOverlayProvider" data-sentry-source-file="index.tsx">
      {children}
      <Overlay customText={customText} onDismiss={() => setShow(false)} show={show} variant={variant} data-sentry-element="Overlay" data-sentry-source-file="index.tsx" />
    </StripeOverlayContext.Provider>;
};
export const useStripeOverlayContext = () => {
  const {
    show: showStripeOverlay,
    setShow: setShowStripeOverlay,
    setVariant: setVariantStripeOverlay,
    setCustomText: setCustomTextStripeOverlay
  } = useContext(StripeOverlayContext);
  return {
    showStripeOverlay,
    setShowStripeOverlay,
    setVariantStripeOverlay,
    setCustomTextStripeOverlay
  };
};